import OrderStatusChip from 'components/composite/OrderStatusChip'
import { AnchorTag } from 'components/core/AnchorTag'
import Column from 'components/core/Column'
import Row from 'components/core/Row'
import Typography from 'components/core/Typography'
import { DESIGN_TOKENS } from 'constants/color.constants'
import { Link2, ArrowRight } from 'lucide-react'
import { OrderStatusType } from 'types/order.types'
import { URLS } from 'utils/url.utils'
import { WidgetComponentProps } from '../../wilsonChat.types'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

export type ShipmentCreatedWidgetData =
    | {
          fullId: string
          orderId: string
          trackingLink: string
          status: OrderStatusType
      }
    | {
          fullId: string
          orderId: string
          trackingLink: string
          status: OrderStatusType
      }[]

export type ShipmentCreatedWidgetProps = WidgetComponentProps<
    'ShipmentCreated',
    ShipmentCreatedWidgetData
>

export const ShipmentCreatedWidget = ({
    widgetData,
}: ShipmentCreatedWidgetProps) => {
    const navigate = useNavigate()

    const shipments = useMemo(
        () => (Array.isArray(widgetData) ? widgetData : [widgetData]),
        [widgetData]
    )

    const [firstShipment] = shipments
    if (!firstShipment) {
        return null
    }

    // Multiple shipments render in a compact way
    if (shipments.length > 1) {
        const fullIds = shipments.map((shipment) => shipment.fullId).join(', ')
        return (
            <Column>
                <AnchorTag
                    color="secondary"
                    title={fullIds}
                    // Keeping href as well in case of right-click -> open in new tab
                    href={URLS.cartage.shipperPortal.wilson(
                        firstShipment.orderId
                    )}
                    onClick={(event) => {
                        event.preventDefault()
                        navigate(
                            URLS.cartage.shipperPortal.wilson(
                                firstShipment.orderId
                            )
                        )
                    }}
                >
                    <Row
                        p="p1"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Row gap="p1">
                            <Link2 size={20} />
                            <div style={{ width: 200 }}>
                                <Typography
                                    shouldTruncate
                                    variant="body2"
                                    color="primary"
                                >
                                    {fullIds}
                                </Typography>
                            </div>
                        </Row>
                        <OrderStatusChip
                            status={firstShipment.status ?? 'quoting'}
                        />
                    </Row>
                </AnchorTag>
            </Column>
        )
    }

    return (
        <Column>
            <AnchorTag
                color="secondary"
                title={firstShipment.fullId}
                // Keeping href as well in case of right-click -> open in new tab
                href={URLS.cartage.shipperPortal.wilson(firstShipment.orderId)}
                onClick={(event) => {
                    event.preventDefault()
                    navigate(
                        URLS.cartage.shipperPortal.wilson(firstShipment.orderId)
                    )
                }}
            >
                <Row p="p1" justifyContent="space-between" alignItems="center">
                    <Row gap="p1">
                        <Link2 size={20} />
                        <Typography variant="body2" color="primary">
                            {firstShipment.fullId}
                        </Typography>
                    </Row>
                    <OrderStatusChip
                        status={firstShipment.status ?? 'quoting'}
                    />
                </Row>
            </AnchorTag>
            <AnchorTag
                color="secondary"
                href={firstShipment.trackingLink}
                target="_blank"
            >
                <Row p="p1" justifyContent="space-between" alignItems="center">
                    <Row gap="p1">
                        <Link2 size={20} />
                        <Typography variant="body2" color="primary">
                            Track your shipment
                        </Typography>
                    </Row>

                    <Row gap="sm" alignItems="center">
                        <Typography isBold variant="body2" color="primary">
                            View
                        </Typography>
                        <ArrowRight
                            size={16}
                            color={DESIGN_TOKENS.text.foreground}
                        />
                    </Row>
                </Row>
            </AnchorTag>
        </Column>
    )
}
